import { Point } from "./matrix";
import { EmbeddedFont } from "./fonts";
import { pathBounds, Segment, serializePath } from "./path";
import { EllipseNode, ImportNode, ImportStyle, ParsedSvg, PathNode, RectNode, TextAlign, TextNode } from "./types";

// Maps parsed SVG nodes onto the builder's own shapes (customRect / customEllipse / customSvg /
// customText / customImage) so imports get the same style panels, save and export as hand-made shapes.

export interface TargetBox {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

export interface ShapeSpec {
  readonly id: string;
  readonly type: "customRect" | "customEllipse" | "customSvg" | "customText" | "customImage";
  readonly x: number;
  readonly y: number;
  readonly rotation: number;
  readonly props: Record<string, unknown>;
}

export interface AssetSpec {
  readonly id: string;
  readonly dataUrl: string;
  readonly mimeType: string;
  readonly w: number;
  readonly h: number;
}

export interface GroupSpec {
  readonly id: string;
  readonly childIds: readonly string[];
}

export interface TextAnchorFix {
  readonly id: string;
  readonly align: TextAlign;
  readonly anchorX: number;
}

// How to restyle a text if its embedded font cannot be installed: the SVG's own family and weight.
export interface FontFallback {
  readonly id: string;
  readonly fontName: string;
  readonly font: string;
  readonly fontWeight: number;
  readonly italic: "italic" | "normal";
}

export interface ShapePlan {
  readonly shapes: readonly ShapeSpec[];
  readonly assets: readonly AssetSpec[];
  // Post-order (innermost first), so each group's children exist before it is created.
  readonly groups: readonly GroupSpec[];
  readonly textAnchors: readonly TextAnchorFix[];
  readonly fonts: readonly EmbeddedFont[];
  readonly fontFallbacks: readonly FontFallback[];
  readonly scale: number;
}

export interface IdFactory {
  readonly shape: () => string;
  readonly asset: () => string;
}

const MIN_SIZE = 1;
// customText renders letterSpacing `|| 1` (so 0 shows as 1px) but measures `|| 0`; a hair above 0 is honoured by both.
const NEAR_ZERO_LETTER_SPACING = 0.01;
const textLetterSpacing = (value: number): number => {
  const rounded = Math.round(value * 100) / 100;
  return rounded === 0 ? NEAR_ZERO_LETTER_SPACING : rounded;
};
const round = (n: number, places = 3): number => {
  const f = 10 ** places;
  return Math.round(n * f) / f;
};

const mix = (color: string): Record<string, { color: string; isHidden: boolean }> => ({
  "0": { color, isHidden: false },
});

class Planner {
  readonly shapes: ShapeSpec[] = [];
  readonly assets: AssetSpec[] = [];
  readonly groups: GroupSpec[] = [];
  readonly textAnchors: TextAnchorFix[] = [];
  readonly fontFallbacks: FontFallback[] = [];

  constructor(
    private readonly s: number,
    private readonly ox: number,
    private readonly oy: number,
    private readonly ids: IdFactory
  ) {}

  private pt(p: Point): Point {
    return { x: this.ox + p.x * this.s, y: this.oy + p.y * this.s };
  }

  private add(spec: Omit<ShapeSpec, "id">): string {
    const id = this.ids.shape();
    this.shapes.push({ ...spec, id, x: round(spec.x), y: round(spec.y), rotation: round(spec.rotation, 6) });
    return id;
  }

  // Shared fill/stroke props for customRect and customEllipse.
  private paintProps(style: ImportStyle): Record<string, unknown> {
    const fill = style.fill?.color;
    const stroke = style.stroke?.color;
    return {
      fill: fill ?? "transparent",
      colorMix: fill ? mix(fill) : {},
      outlineColor: stroke ?? "#000000",
      outlineColorMix: stroke ? mix(stroke) : {},
      strokeWidth: stroke ? round(style.strokeWidth * this.s, 2) : 0,
      // The builder's "outside" stroke is centred on the edge, matching SVG.
      strokeAlign: "outside",
      transparency: round(style.opacity * (style.fill?.opacity ?? style.stroke?.opacity ?? 1), 3),
    };
  }

  box(node: RectNode | EllipseNode): string {
    const origin = this.pt(node);
    const props: Record<string, unknown> = {
      w: Math.max(MIN_SIZE, round(node.w * this.s)),
      h: Math.max(MIN_SIZE, round(node.h * this.s)),
      ...this.paintProps(node.style),
    };
    if (node.kind === "rect") props.rounded = round(node.radius * this.s, 2);
    const type = node.kind === "rect" ? "customRect" : "customEllipse";
    return this.add({ type, x: origin.x, y: origin.y, rotation: node.rotation, props });
  }

  private svgShape(segments: Segment[], style: ImportStyle, mode: "fill" | "stroke"): string | null {
    const bounds = pathBounds(segments);
    if (!bounds) return null;
    const strokeWidth = style.strokeWidth * this.s;
    const pad = mode === "stroke" ? strokeWidth / 2 : 0;
    const x = bounds.minX - pad;
    const y = bounds.minY - pad;
    const w = Math.max(MIN_SIZE, bounds.maxX - bounds.minX + 2 * pad);
    const h = Math.max(MIN_SIZE, bounds.maxY - bounds.minY + 2 * pad);
    const paint = mode === "fill" ? style.fill : style.stroke;
    if (!paint) return null;
    const rootAttrs =
      mode === "fill"
        ? `fill="${paint.color}"`
        : [
            `fill="none" stroke="${paint.color}" stroke-width="${round(strokeWidth, 3)}"`,
            style.lineCap && /^(butt|round|square)$/.test(style.lineCap) ? `stroke-linecap="${style.lineCap}"` : "",
            style.lineJoin && /^(miter|round|bevel)$/.test(style.lineJoin) ? `stroke-linejoin="${style.lineJoin}"` : "",
          ]
            .filter(Boolean)
            .join(" ");
    const fillRule = mode === "fill" && style.fillRule === "evenodd" ? ' fill-rule="evenodd"' : "";
    const viewBox = [x, y, w, h].map((n) => round(n)).join(" ");
    const svgPath =
      `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${viewBox}" width="${round(w)}" height="${round(h)}" ` +
      `${rootAttrs}><path d="${serializePath(segments)}"${fillRule}/></svg>`;
    return this.add({
      type: "customSvg",
      x,
      y,
      rotation: 0,
      props: {
        w: round(w),
        h: round(h),
        svgPath,
        fill: paint.color,
        colorMix: mix(paint.color),
        transparency: round(style.opacity * paint.opacity, 3),
      },
    });
  }

  path(node: PathNode): string[] {
    const segments = node.segments.map((seg): Segment => {
      if (seg.type === "Z") return seg;
      if (seg.type === "C") return { type: "C", c1: this.pt(seg.c1), c2: this.pt(seg.c2), p: this.pt(seg.p) };
      return { type: seg.type, p: this.pt(seg.p) };
    });
    // The builder's customSvg has one colour, so fill + stroke become two shapes (stroke on top).
    const fill = node.style.fill ? this.svgShape(segments, node.style, "fill") : null;
    const stroke = node.style.stroke ? this.svgShape(segments, node.style, "stroke") : null;
    return [fill, stroke].filter((id): id is string => id !== null);
  }

  text(node: TextNode): string {
    const origin = this.pt(node);
    const size = round(node.fontSize * this.s, 1);
    const longest = Math.max(...node.lines.map((l) => l.length));
    const italic = node.italic ? "italic" : "normal";
    // An embedded face is installed as its own font, so it is used at its natural weight.
    const face = node.embeddedFont
      ? { font: node.embeddedFont, fontWeight: 400, italic: "normal" as const }
      : { font: node.fontFamily, fontWeight: node.fontWeight, italic };
    const id = this.add({
      type: "customText",
      x: origin.x,
      y: origin.y,
      rotation: node.rotation,
      props: {
        text: node.lines.join("\n"),
        size,
        ...face,
        textAlign: node.align,
        fill: node.color,
        colorMix: mix(node.color),
        transparency: round(node.opacity, 3),
        lineHeight: round(node.lineHeight, 3),
        letterSpacing: textLetterSpacing(node.letterSpacing * this.s),
        autoSize: true,
        w: Math.max(MIN_SIZE, round(longest * size * 0.6)),
      },
    });
    if (node.embeddedFont) {
      this.fontFallbacks.push({
        id,
        fontName: node.embeddedFont,
        font: node.fontFamily,
        fontWeight: node.fontWeight,
        italic,
      });
    }
    if (node.align !== "start" && Math.abs(node.rotation) < 1e-6) {
      this.textAnchors.push({ id, align: node.align, anchorX: round(this.pt(node.anchor).x) });
    }
    return id;
  }

  node(node: ImportNode): string[] {
    switch (node.kind) {
      case "rect":
      case "ellipse":
        return [this.box(node)];
      case "path":
        return wrap(this, this.path(node));
      case "text":
        return [this.text(node)];
      case "image": {
        const assetId = this.ids.asset();
        this.assets.push({ id: assetId, dataUrl: node.href, mimeType: node.mimeType, w: node.w, h: node.h });
        const origin = this.pt(node);
        return [
          this.add({
            type: "customImage",
            x: origin.x,
            y: origin.y,
            rotation: node.rotation,
            props: {
              assetId,
              w: Math.max(MIN_SIZE, round(node.w * this.s)),
              h: Math.max(MIN_SIZE, round(node.h * this.s)),
              transparency: round(node.opacity, 3),
              ...(node.crop ? { crop: node.crop } : {}),
            },
          }),
        ];
      }
      case "group":
        return wrap(
          this,
          node.children.flatMap((child) => this.node(child))
        );
      default:
        return [];
    }
  }

  group(childIds: string[]): string {
    const id = this.ids.shape();
    this.groups.push({ id, childIds });
    return id;
  }
}

const wrap = (planner: Planner, ids: string[]): string[] => (ids.length > 1 ? [planner.group(ids)] : ids);

/** Fits the parsed SVG into `target` (contain, centred) and plans the builder shapes to create. */
export const planShapes = (parsed: ParsedSvg, target: TargetBox, ids: IdFactory): ShapePlan => {
  const s = Math.min(target.w / parsed.width, target.h / parsed.height);
  const scale = Number.isFinite(s) && s > 0 ? s : 1;
  const ox = target.x + (target.w - parsed.width * scale) / 2;
  const oy = target.y + (target.h - parsed.height * scale) / 2;
  const planner = new Planner(scale, ox, oy, ids);
  parsed.nodes.forEach((node) => planner.node(node));
  return {
    shapes: planner.shapes,
    assets: planner.assets,
    groups: planner.groups,
    textAnchors: planner.textAnchors,
    fonts: parsed.fonts,
    fontFallbacks: planner.fontFallbacks,
    scale,
  };
};
